import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { OrganizationAction, OrganizationState } from '@state/organization.state';
import { AnalyticCategoryWithEntities } from 'app/@types/shared/analytical-categories-entities';
import { AnalyticalCategoryDto } from 'app/@types/dto/analytical-category-dto.model';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { ConfirmationService } from 'primeng/api';
import { Observable, Subject, takeUntil } from 'rxjs';
import { destructiveConfirmation } from 'app/main/helpers/confirmation';
import { revealFormErrors } from 'app/main/helpers/form-validation';

@Component({
    selector: 'app-edit-analytic-category',
    templateUrl: './edit-analytic-category.component.html',
    styleUrls: ['./edit-analytic-category.component.scss'],
    providers: [ConfirmationService],
    standalone: false,
})
export class EditAnalyticCategoryComponent implements OnInit, OnDestroy {
    public activityForm: FormGroup;
    private ngUnsubscribe = new Subject<void>();

    @Input() selectedAnalyticCategory?: AnalyticCategoryWithEntities;
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    @Input() organizationId: string;
    @Output() closeEditActivity = new EventEmitter<{ translateKey: string; status: boolean } | undefined>();

    @Select(OrganizationState.getEntities) entities$!: Observable<OrganizationEntityDto[]>;
    entitiesOptions: OrganizationEntityDto[] = [];

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly confirmationService: ConfirmationService,
        private readonly translateService: CustomTranslateService,
        private readonly store: Store,
        private readonly host: ElementRef<HTMLElement>,
    ) {
        this.activityForm = this.formBuilder.group({
            name: new FormControl('', Validators.required),
            description: new FormControl(''),
            entities: new FormControl<OrganizationEntityDto[] | null>(null),
        });
    }

    ngOnInit(): void {
        this.entities$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((entities) => {
            this.entitiesOptions = entities;
        });
        if (this.selectedAnalyticCategory) {
            this.activityForm.patchValue({
                name: this.selectedAnalyticCategory.name,
                description: this.selectedAnalyticCategory.description,
                entities: this.selectedAnalyticCategory.entities,
            });
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    save() {
        if (!revealFormErrors(this.activityForm, this.host.nativeElement)) {
            return;
        }

        const analyticalCategory: AnalyticalCategoryDto = {
            id: this.selectedAnalyticCategory?.id ?? undefined,
            name: this.activityForm.get('name')?.value as string,
            description: this.activityForm.get('description')?.value as string,
            entityIds:
                (this.activityForm.get('entities')?.value as OrganizationEntityDto[] | undefined)?.map(
                    (entity: OrganizationEntityDto) => entity.id ?? '',
                ) ?? [],
            default: this.selectedAnalyticCategory?.default ?? false,
        };

        if (this.selectedAnalyticCategory) {
            this.editAnalyticCategory(analyticalCategory);
        } else {
            this.createAnalyticCategory(analyticalCategory);
        }
    }

    private createAnalyticCategory(analyticalCategory: AnalyticalCategoryDto) {
        const callCompletion: Subject<void> = new Subject();
        this.store.dispatch(
            new OrganizationAction.CreateAnalyticCategory(this.organizationId, analyticalCategory, callCompletion),
        );
        callCompletion.subscribe({
            next: () => {
                this.closeEditActivity.emit({
                    translateKey: 'settings.analytical.toasts.createSuccess',
                    status: true,
                });
            },
            error: () => {
                this.closeEditActivity.emit({
                    translateKey: 'settings.analytical.toasts.createFail',
                    status: false,
                });
            },
        });
    }

    private editAnalyticCategory(newAnalyticCategory: AnalyticalCategoryDto) {
        const callCompletion: Subject<void> = new Subject();
        this.store.dispatch(
            new OrganizationAction.UpdateAnalyticCategory(this.organizationId, newAnalyticCategory, callCompletion),
        );
        callCompletion.subscribe({
            next: () => {
                this.closeEditActivity.emit({
                    translateKey: 'settings.analytical.toasts.updateSuccess',
                    status: true,
                });
            },
            error: () => {
                this.closeEditActivity.emit({
                    translateKey: 'settings.analytical.toasts.updateFail',
                    status: false,
                });
            },
        });
    }

    onDelete(): void {
        this.confirmationService.confirm(
            destructiveConfirmation({
                message: this.translateService.instant('common.confirm_message'),
                header: this.translateService.instant('common.confirm_title'),
                acceptLabel: this.translateService.instant('common.confirm_button'),
                rejectLabel: this.translateService.instant('common.cancel_button'),
                icon: 'ms ms-warning',
                accept: () => {
                    if (this.selectedAnalyticCategory?.id) {
                        const callCompletion: Subject<void> = new Subject();
                        this.store.dispatch(
                            new OrganizationAction.DeleteAnalyticCategory(
                                this.organizationId,
                                this.selectedAnalyticCategory.id,
                                callCompletion,
                            ),
                        );
                        callCompletion.subscribe({
                            next: () => {
                                this.closeEditActivity.emit({
                                    translateKey: 'settings.analytical.toasts.deleteSuccess',
                                    status: true,
                                });
                            },
                            error: () => {
                                this.closeEditActivity.emit({
                                    translateKey: 'settings.analytical.toasts.deleteFail',
                                    status: false,
                                });
                            },
                        });
                    }
                },
            }),
        );
    }

    close(): void {
        this.closeEditActivity.emit();
    }

    /**
     * Generates a tooltip string from an array of entities.
     * Supports both strings and objects with a 'name' property.
     * Only includes entities beyond the first two.
     *
     * @param entities - An array of either strings or objects containing a 'name' field
     * @returns A comma-separated string of the remaining entity names (after the first two)
     */
    getTooltipEntities(entities: (string | { name: string })[]): string {
        return entities
            .slice(2) // Skip the first two entities (already displayed visually)
            .map(
                (e) =>
                    typeof e === 'string'
                        ? e // If entity is a string, use it directly
                        : e.name, // If it's an object, extract the 'name' property
            )
            .join(', '); // Combine remaining names into a single string separated by commas
    }
}
